import type { AIIntakeReport } from "./ai-intake";
import type { FolderDiagnosis, FolderUploadSummary, SelectedFile } from "./folder";

const DATABASE_NAME = "doorway-pending-publish";
const DATABASE_VERSION = 1;
const STORE_NAME = "drafts";
const DRAFT_TTL_MS = 30 * 60 * 1000;
const DRAFT_ID_PATTERN = /^[a-zA-Z0-9_-]{8,128}$/;

export interface ProjectFlowDraft {
  files: SelectedFile[];
  omitted: string[];
  uploadSummary: FolderUploadSummary;
  diagnosis: FolderDiagnosis;
  aiReport: AIIntakeReport;
  visibility: "public" | "password";
}

interface StoredFile {
  blob: Blob;
  name: string;
  path: string;
  type: string;
  lastModified: number;
}

interface StoredDraft extends Omit<ProjectFlowDraft, "files"> {
  id: string;
  createdAt: number;
  files: StoredFile[];
}

export function createPendingPublishId(): string {
  return crypto.randomUUID().replaceAll("-", "");
}

export async function savePendingPublishDraft(id: string, draft: ProjectFlowDraft): Promise<void> {
  assertDraftId(id);
  const stored: StoredDraft = {
    ...draft,
    id,
    createdAt: Date.now(),
    files: draft.files.map(({ file, path }) => ({
      blob: file,
      name: file.name,
      path,
      type: file.type,
      lastModified: file.lastModified,
    })),
  };
  const database = await openDatabase();
  try {
    await completeTransaction(database, "readwrite", (store) => store.put(stored));
  } finally {
    database.close();
  }
}

export async function takePendingPublishDraft(id: string): Promise<ProjectFlowDraft | null> {
  if (!DRAFT_ID_PATTERN.test(id)) return null;
  const database = await openDatabase();
  try {
    const stored = await takeStoredDraft(database, id);
    if (!stored || Date.now() - stored.createdAt > DRAFT_TTL_MS || !isStoredDraft(stored)) return null;
    return {
      files: stored.files.map((item) => ({
        file: new File([item.blob], item.name, { type: item.type, lastModified: item.lastModified }),
        path: item.path,
      })),
      omitted: stored.omitted,
      uploadSummary: stored.uploadSummary,
      diagnosis: stored.diagnosis,
      aiReport: stored.aiReport,
      visibility: stored.visibility,
    };
  } finally {
    database.close();
  }
}

export async function discardPendingPublishDraft(id: string): Promise<void> {
  if (!DRAFT_ID_PATTERN.test(id)) return;
  const database = await openDatabase();
  try {
    await completeTransaction(database, "readwrite", (store) => { store.delete(id); });
  } finally {
    database.close();
  }
}

function assertDraftId(id: string): void {
  if (!DRAFT_ID_PATTERN.test(id)) throw new Error("Doorway could not prepare this folder for sign-in.");
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener("upgradeneeded", () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: "id" });
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => reject(request.error ?? new Error("Doorway could not save this folder locally.")));
    request.addEventListener("blocked", () => reject(new Error("Doorway could not open local browser storage.")));
  });
}

function completeTransaction(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    operation(transaction.objectStore(STORE_NAME));
    transaction.addEventListener("complete", () => resolve());
    transaction.addEventListener("abort", () => reject(transaction.error ?? new Error("Doorway could not save this folder locally.")));
    transaction.addEventListener("error", () => reject(transaction.error ?? new Error("Doorway could not save this folder locally.")));
  });
}

function takeStoredDraft(database: IDBDatabase, id: string): Promise<StoredDraft | null> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);
    request.addEventListener("success", () => {
      const value = request.result as StoredDraft | undefined;
      store.delete(id);
      transaction.addEventListener("complete", () => resolve(value ?? null));
    });
    request.addEventListener("error", () => reject(request.error ?? new Error("Doorway could not restore this folder.")));
    transaction.addEventListener("abort", () => reject(transaction.error ?? new Error("Doorway could not restore this folder.")));
  });
}

function isStoredDraft(value: StoredDraft): boolean {
  return Array.isArray(value.files)
    && value.files.every((item) => item.blob instanceof Blob && typeof item.name === "string" && typeof item.path === "string")
    && Array.isArray(value.omitted)
    && typeof value.uploadSummary === "object"
    && typeof value.diagnosis === "object"
    && typeof value.aiReport === "object"
    && (value.visibility === "public" || value.visibility === "password");
}
